import React, { useState, useEffect, useRef, useCallback } from 'react';


const ImageCarousel = ({
    items = [],
    initialIndex = 0,
    onClose,
    onDeleteItem
}) => {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const [zoomLevel, setZoomLevel] = useState(1);
    const [isDragging, setIsDragging] = useState(false);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [startDragPos, setStartDragPos] = useState({ x: 0, y: 0 });
    const mediaRef = useRef(null);
    const videoRef = useRef(null);

    const currentItem = items[currentIndex] || null;
    const isVideo = currentItem?.type === 'video';
    const isImage = currentItem?.type === 'image';

    // --- Reset state when items/index change ---
    useEffect(() => {
        videoRef.current?.pause();
        setCurrentIndex(initialIndex);
        setZoomLevel(1);
        setPosition({ x: 0, y: 0 });
    }, [items, initialIndex]);

    // --- Effect to programmatically Play Video when ready ---
    useEffect(() => {
        if (isVideo && videoRef.current) {
            // Attempt to play programmatically
            const playPromise = videoRef.current.play();

            if (playPromise !== undefined) {
                playPromise.then(() => {
                    console.log("Video autoplay started programmatically.");
                }).catch(error => {
                    console.warn("Video autoplay prevented programmatically:", error);
                });
            }
        }
        // This effect should re-run when the current item changes (specifically type or src)
        // or when the videoRef becomes available.
    }, [currentItem, isVideo]); // Removed videoRef from dependencies, it's used in the check


    const handleNext = useCallback(() => {
        if (currentIndex < items.length - 1) {
            videoRef.current?.pause();
            setCurrentIndex(prev => prev + 1);
        }
    }, [currentIndex, items.length]);

    const handlePrev = useCallback(() => {
        if (currentIndex > 0) {
             videoRef.current?.pause();
            setCurrentIndex(prev => prev - 1);
        }
    }, [currentIndex]);

    const handleZoomIn = () => {
        if (isImage) setZoomLevel(prev => Math.min(prev + 0.5, 5));
    };

    const handleZoomOut = () => {
        if (isImage) {
            const newZoom = Math.max(zoomLevel - 0.5, 1);
            setZoomLevel(newZoom);
            if (newZoom === 1) setPosition({ x: 0, y: 0 });
        }
    };

    const handleMouseDown = (e) => {
        if (!isImage || zoomLevel <= 1) return;
        e.preventDefault();
        setIsDragging(true);
        setStartDragPos({ x: e.clientX - position.x, y: e.clientY - position.y });
        if (mediaRef.current) mediaRef.current.style.cursor = 'grabbing';
    };

    const handleMouseMove = useCallback((e) => {
        if (!isDragging || !isImage || zoomLevel <= 1) return;
        e.preventDefault();
        setPosition({ x: e.clientX - startDragPos.x, y: e.clientY - startDragPos.y });
    }, [isDragging, startDragPos, isImage, zoomLevel]);

    const handleMouseUp = useCallback(() => {
        if (isDragging) {
            setIsDragging(false);
            if (mediaRef.current) mediaRef.current.style.cursor = 'grab';
        }
    }, [isDragging]);

    const handleMouseLeave = useCallback(() => { if (isDragging) handleMouseUp(); }, [isDragging, handleMouseUp]);

    useEffect(() => { if (isDragging) { window.addEventListener('mousemove', handleMouseMove); window.addEventListener('mouseup', handleMouseUp); window.addEventListener('mouseleave', handleMouseLeave); } else { window.removeEventListener('mousemove', handleMouseMove); window.removeEventListener('mouseup', handleMouseUp); window.removeEventListener('mouseleave', handleMouseLeave); } return () => { window.removeEventListener('mousemove', handleMouseMove); window.removeEventListener('mouseup', handleMouseUp); window.removeEventListener('mouseleave', handleMouseLeave); }; }, [isDragging, handleMouseMove, handleMouseUp, handleMouseLeave]);

    useEffect(() => { const handleKeyDown = (e) => { if (e.key === 'ArrowRight') handleNext(); else if (e.key === 'ArrowLeft') handlePrev(); else if (e.key === 'Escape') onClose(); }; window.addEventListener('keydown', handleKeyDown); return () => window.removeEventListener('keydown', handleKeyDown); }, [handleNext, handlePrev, onClose]);

    if (!currentItem) return null;

    return (
        <div className="relative z-[100]" aria-label="media carousel" role="dialog" aria-modal="true">
            <div className="fixed inset-0 bg-black/80 backdrop-blur-sm transition-opacity duration-300 ease-in-out" onClick={onClose}></div>

            <div className="fixed inset-0 z-10 overflow-hidden">
                <div className="h-full flex flex-col">
                    <div className="w-full flex justify-end p-4 md:p-5 absolute top-0 right-0 z-30">
                         {isImage && (
                            <>
                                <button onClick={handleZoomOut} disabled={zoomLevel <= 1} className="group flex justify-center items-center outline-none rounded-full focus:outline-none transition-all duration-200 mr-3 md:mr-4 disabled:opacity-50" title="Zoom out" aria-label="Zoom out">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" aria-hidden="true" className="w-6 h-6 text-white opacity-80 hover:opacity-100"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607zM13.5 10.5h-6"></path></svg>
                                </button>
                                <button onClick={handleZoomIn} disabled={zoomLevel >= 5} className="group flex justify-center items-center outline-none rounded-full focus:outline-none transition-all duration-200 mr-3 md:mr-4 disabled:opacity-50" title="Zoom in" aria-label="Zoom in">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" aria-hidden="true" className="w-6 h-6 text-white opacity-80 hover:opacity-100"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607zM10.5 7.5v6m3-3h-6"></path></svg>
                                </button>
                            </>
                         )}
                        <button onClick={onClose} className="group flex justify-center items-center outline-none rounded-full focus:outline-none transition-all duration-200" title="Close carousel" aria-label="Close carousel">
                             <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" aria-hidden="true" className="w-6 h-6 text-white opacity-80 hover:opacity-100"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"></path></svg>
                        </button>
                    </div>

                    <div className="relative w-full h-full flex items-center justify-center overflow-hidden p-4 md:p-8">
                        {currentIndex > 0 && ( <button onClick={handlePrev} className="group flex justify-center items-center rounded-full outline-none focus:outline-none bg-black/20 hover:bg-black/40 active:bg-black/50 transition-all duration-200 ease absolute top-1/2 transform -translate-y-1/2 z-30 left-2 md:left-5 p-2 md:p-3" title="Previous" aria-label="Previous item"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="w-6 h-6 text-white opacity-80 group-hover:opacity-100"><path fillRule="evenodd" d="M7.72 12.53a.75.75 0 010-1.06l7.5-7.5a.75.75 0 111.06 1.06L9.31 12l6.97 6.97a.75.75 0 11-1.06 1.06l-7.5-7.5z" clipRule="evenodd"></path></svg></button> )}

                        <div
                            ref={mediaRef}
                            className="relative transition-transform duration-200 flex items-center justify-center h-full w-full"
                            style={{
                                transform: isImage ? `scale(${zoomLevel}) translate(${position.x}px, ${position.y}px)` : 'scale(1)',
                                cursor: isImage && zoomLevel > 1 ? 'grab' : 'default',
                                userSelect: isDragging ? 'none' : 'auto',
                            }}
                            onMouseDown={handleMouseDown}
                        >
                            {isImage && (
                                 <img
                                    className={`block max-w-full max-h-full object-contain transition-[transform,opacity] duration-200 ${ isDragging ? 'pointer-events-none' : '' }`}
                                    src={currentItem.src}
                                    alt={currentItem.alt || `Image ${currentIndex + 1}`}
                                    draggable="false"
                                />
                            )}
                            {isVideo && (
                                <video
                                    ref={videoRef}
                                    key={currentItem.src}
                                    className="block max-w-full max-h-full object-contain"
                                    controls
                                    autoPlay
                                    muted
                                    poster={currentItem.thumbnailUrl}
                                    preload="metadata"
                                    onEnded={() => videoRef.current?.load()}
                                >
                                    <source src={currentItem.src} type="video/mp4" />
                                    Your browser does not support the video tag.
                                </video>
                            )}
                        </div>

                        {currentIndex < items.length - 1 && ( <button onClick={handleNext} className="group flex justify-center items-center rounded-full outline-none focus:outline-none bg-black/20 hover:bg-black/40 active:bg-black/50 transition-all duration-200 ease absolute top-1/2 transform -translate-y-1/2 z-30 right-2 md:right-5 p-2 md:p-3" title="Next" aria-label="Next item"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="w-6 h-6 text-white opacity-80 group-hover:opacity-100"><path fillRule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clipRule="evenodd"></path></svg></button> )}
                    </div>
                     {items.length > 1 && ( <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/50 text-white text-xs px-2 py-1 rounded-full"> {currentIndex + 1} / {items.length} </div> )}
                </div>
            </div>
        </div>
    );
};

export { ImageCarousel };